import { useEffect, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { LIVE_URL } from "../../../config/config";
import useAuthToken from "../../../hooks/useAuthToken";
import Spinner from "../spinner/Spinner";
import styles from "./css/AddReport.module.scss";

const AddReport = () => {
  const { id } = useParams();
  const { token } = useAuthToken();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [errors, setErrors] = useState({});
  const [fileName, setFileName] = useState("No file chosen");
  const [filePreview, setFilePreview] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const MAX_FILE_SIZE = 5 * 1024 * 1024;

  // Function to reset form fields
  const resetForm = () => {
    setTitle("");
    setSelectedStatus("");
    setFileName("No file chosen");
    setFilePreview("");
    setPopupMessage("");
    setErrors({});
  };

  useEffect(() => {
    if (id) {
      const fetchReportData = async () => {
        try {
          setLoading(true);
          const response = await fetch(
            `${LIVE_URL}/admin/report-category/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = await response.json();
          if (response.ok) {
            const reportData = data.data;
            setTitle(reportData.title);
            setSelectedStatus(reportData.status ? "true" : "false");
            setFilePreview(reportData.icon);
            setFileName(reportData.icon);
          }
        } catch (error) {
          console.error("Error fetching report data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchReportData();
    } else {
      resetForm();
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    let timer;
    if (popupMessage) {
      const popupElement = document.getElementById("popup-message");
      if (popupElement) {
        popupElement.style.opacity = "1";
      }
      timer = setTimeout(() => {
        if (popupElement) {
          popupElement.style.opacity = "0";
        }
        setTimeout(() => {
          setPopupMessage("");
        }, 500);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [popupMessage]);

  const handleSelectChange = (event) => {
    setSelectedStatus(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, selectedStatus: "" }));
  };

  const handleTitleChange = (event) => {
    setTitle(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, title: "" }));
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const fileName = selectedFile.name.toLowerCase();
      const fileExtension = fileName.split(".").pop();

      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/svg+xml",
        "image/heic",
      ];
      const validExtensions = ["jpg", "jpeg", "png", "webp", "svg", "heic"];

      if (
        !validTypes.includes(fileType) ||
        !validExtensions.includes(fileExtension)
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          file: "Only valid image files are allowed (JPG,JPEG, PNG, SVG, WEBP,HEIC).",
        }));
        setFileName("No file chosen");
        setFile(null);
        setFilePreview("");
        return;
      }

      if (selectedFile.size > MAX_FILE_SIZE) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          file: "File size exceeds 5MB limit.",
        }));
        setFileName("No file chosen");
        setFile(null);
        setFilePreview("");
      } else {
        setFileName(selectedFile.name);
        setFile(selectedFile);
        setFilePreview("");
        setErrors((prevErrors) => ({ ...prevErrors, file: "" }));
      }
    } else {
      setFileName("No file chosen");
      setFile(null);
      setFilePreview("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const newErrors = {};
    if (!title) {
      newErrors.title = "❗ Don't Forget the Title!";
    }
    if (!selectedStatus)
      newErrors.selectedStatus = "❗Status field cannot be blank.";
    setErrors(newErrors);
    if (!file && !id) newErrors.file = "❗ Don't forget to add an image!";
    setErrors(newErrors);
    if (errors.file) {
      setPopupMessage("Please select the files.");
      setLoading(false);
      return;
    }
    if (Object.keys(newErrors).length > 0) {
      setPopupMessage("Please fill in all required fields");
      setLoading(false);
      return;
    }

    const payload = {
      title: title,
      status: selectedStatus === "true",
    };

    if (id) {
      payload.id = id;
    }

    const formData = new FormData();
    formData.append("title", title);
    if (id) formData.append("id", id);
    formData.append("type", "report-category");
    formData.append("status", selectedStatus === "true");
    if (file) {
      formData.append("image", file);
    }
    if (id) {
      formData.append("id", id);
    }
    try {
      const url = id
        ? `${LIVE_URL}/admin/report-category/update`
        : `${LIVE_URL}/admin/report-category/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const successMessage = id
          ? "Report updated successfully."
          : "Report added successfully.";
        navigate(
          `/home/list-report?message=${encodeURIComponent(successMessage)}`,
        );
      } else {
        setPopupMessage(`Failed to save report. Status: ${response.status}`);
      }
    } catch (error) {
      setPopupMessage("Error saving report:", error);
    } finally {
      setLoading(false);
    }
  };

  const options = [
    { value: "true", label: "Publish" },
    { value: "false", label: "UnPublish" },
  ];

  if (loading) {
    return <Spinner />;
  }
  return (
    <>
      <div
        id="popup-message"
        className={`${styles.update_message} ${popupMessage.includes()}`}
      >
        {popupMessage && <p>{popupMessage}</p>}
      </div>
      <div className={`${styles.add_religion}`}>
        <header>
          <h2>{id ? "Update Report" : "Add Report"}</h2>
        </header>
        <section className={`${styles.section}`}>
          <label htmlFor="">Report Image</label>
          <div className={`${styles.fileInputContainer}`}>
            <input type="file" id="file" onChange={handleFileChange} />
            <label htmlFor="file">Choose File</label>
            <span id="file-name">{fileName}</span>
          </div>

          {errors.file && <p className={styles.error}>{errors.file}</p>}
          <br />
          <label htmlFor="title">Report Title</label>
          <br />
          <input
            type="text"
            id="title"
            value={title}
            onChange={handleTitleChange}
          />
          {errors.title && <p className={styles.error}>{errors.title}</p>}
          <br />
          <br />
          <label htmlFor="status">Report Status</label>
          <br />
          <div className={styles.select_container}>
            <select value={selectedStatus} onChange={handleSelectChange}>
              <option value="">Select Status</option>
              {options.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FaChevronDown className={`${styles.chevron_down}`} />
          </div>
          {errors.selectedStatus && (
            <p className={styles.error}>{errors.selectedStatus}</p>
          )}
          <div className={styles.button_container}>
            <button onClick={() => navigate("/home/list-report")}>
              Cancel
            </button>
            <button onClick={handleSubmit}>
              {id ? "Update Report" : "Add Report"}
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default AddReport;
